// Copyright ©️ 2026 eVolve MEP, LLC

import { type TaskPaginated, type Task, type TaskId, useEvolvePaginatedGet, useEvolvePatch } from '@evolvemep/api-ts';
import { Flex, Input } from '@mantine/core';
import { useForm } from '@mantine/form';

import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { SearchableCheckList } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableCheckList';

type Props = {
  opened: boolean;
  onClose: () => void;
  task: Task | TaskPaginated;
  onTaskUpdated?: (updatedTask: Task) => void;
};

type FormValues = {
  taskPredecessorIds: TaskId[];
};

const PredecessorFields = ({ task, onTaskUpdated }: Pick<Props, 'task' | 'onTaskUpdated'>) => {
  const paginatedGet = useEvolvePaginatedGet(`shop/task/${task.taskId}/viablePredecessors`);
  const { apiCall: updateTask } = useEvolvePatch(`shop/task/${task.taskId}`);
  const form = useForm<FormValues>({
    initialValues: {
      taskPredecessorIds: task.taskPredecessors.map((predecessor) => predecessor.taskId),
    },
  });

  const onSubmit = async (values: typeof form.values) => {
    await updateTask(values).then(onTaskUpdated);
  };

  useFormPopover(form, onSubmit);

  return (
    <Flex direction="column" gap={3}>
      <Input.Label>Predecessors for {task.taskName}</Input.Label>
      <SearchableCheckList
        idKey="taskId"
        nothingFoundMessage="No tasks found."
        getItemLabel={(t) => t.taskName}
        paginatedGet={paginatedGet}
        defaultValue={task.taskPredecessors}
        onChange={(selectedTasks) =>
          form.setFieldValue(
            'taskPredecessorIds',
            selectedTasks.map((selectedTask) => selectedTask.taskId),
          )
        }
        excludeOption={(o) => o.taskId === task.taskId}
        useLike={false}
        searchKey="taskName"
        textInputProps={{ size: 'xs', placeholder: 'Type to search', style: { width: '100%' } }}
        flexListProps={{
          gap: 'xs',
          style: {
            height: 200,
          },
        }}
      />
    </Flex>
  );
};

export const SetTaskPredecessorsPopover = ({ opened, onClose, task, onTaskUpdated }: Props) => (
  <FormPopover opened={opened} onClose={onClose}>
    <PredecessorFields task={task} onTaskUpdated={onTaskUpdated} />
  </FormPopover>
);
